import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type NotificationsResult } from '@whinerp/client';
import { Icon, IconButton, Num, Rule, cx } from '@whinerp/ui';
import {
  OFFERED_LOCALES,
  OFFERED_TIME_ZONES,
  formatCount,
  formatDate,
  formatMoney,
} from '@whinerp/contracts/format';
import { SyncStatus } from '../offline/SyncStatus.js';
import { useFormat, useI18n, useT } from '../app/i18n.js';
import { useSession } from '../app/session.js';
import { useTheme } from '../app/theme.js';

/**
 * The top bar, on every authenticated screen.
 *
 * Search, the command palette, notifications, setup and the account menu. No trial banner: commercial pressure occupying the top of every screen
 * reads as desperation and steals vertical space on the laptops most people
 * are using. Trial status lives in the account menu, where somebody looking for
 * it will look.
 */

export function TopBar({
  onOpenSearch,
  onOpenPalette,
  onOpenNotifications,
}: {
  onOpenSearch: () => void;
  onOpenPalette: () => void;
  onOpenNotifications: () => void;
}): JSX.Element {
  const t = useT();
  const { session } = useSession();

  return (
    <header
      data-component="TopBar"
      className="sticky top-0 z-30 flex h-topbar shrink-0 items-center gap-3 border-b border-b-rule bg-surface px-4"
    >
      <WorkspaceSwitcher />

      <button
        type="button"
        onClick={onOpenSearch}
        data-component="GlobalSearch"
        className={cx(
          'flex h-input min-w-0 flex-1 items-center gap-2 rounded-control border border-control',
          'bg-canvas px-input-x text-body text-ink-300 transition-colors duration-state ease-out',
          'hover:border-bronze md:max-w-reading',
        )}
      >
        <Icon name="MagnifyingGlass" size="inline" decorative />
        <span className="truncate">{t('topbar.searchPlaceholder')}</span>
      </button>

      <div className="ms-auto flex items-center gap-1">
        {/* Before the other controls, and only when it has something to say.
            What this device is still holding is the one thing on this bar that
            changes what a person should do next. */}
        <SyncStatus />
        <IconButton icon="Command" label={t('topbar.commandPalette')} onClick={onOpenPalette} />
        <NotificationBell onOpen={onOpenNotifications} />
        {/*
          * No Help button.
          *
          * It was `<IconButton icon="Question" label="Help" />` with no
          * onClick: a question mark on all 37 authenticated screens that did
          * nothing when pressed. There is no help content to open, and a
          * control that renders and does nothing is indistinguishable from the
          * ones beside it that work. It comes back when there is something
          * behind it (D-227).
          */}
        <Link
          to="/setup"
          aria-label={t('topbar.setup')}
          title={t('topbar.setup')}
          className="inline-flex h-touch w-touch items-center justify-center rounded-control text-ink-600 hover:bg-sunken hover:text-ink"
        >
          <Icon name="Gear" decorative />
        </Link>
        <AvatarMenu />
      </div>

      <span className="sr-only">{session.workspace.name}</span>
    </header>
  );
}

/**
 * The workspace switcher.
 *
 * One workspace per session in this build, so it names the current one rather
 * than pretending to offer a list that does not exist. When a person genuinely
 * belongs to two, this is where they will look.
 */
export function WorkspaceSwitcher(): JSX.Element {
  const { session } = useSession();
  return (
    <div
      data-component="WorkspaceSwitcher"
      className="flex min-w-0 items-center gap-2 rounded-control px-2 py-1"
    >
      <span
        aria-hidden
        className="flex h-icon w-icon shrink-0 items-center justify-center rounded-control bg-bronze text-meta font-medium text-bronze-on"
      >
        {session.workspace.name.slice(0, 1).toUpperCase()}
      </span>
      <span className="hidden min-w-0 truncate text-body font-medium text-ink md:block">
        {session.workspace.name}
      </span>
    </div>
  );
}

/**
 * The notification bell.
 *
 * The unread count is a number a person reads, so it goes through the
 * formatter like every other one, and the label says how many rather than
 * leaving a screen reader to announce a bare digit.
 */
export function NotificationBell({ onOpen }: { onOpen: () => void }): JSX.Element {
  const t = useT();
  const format = useFormat();

  /*
   * The count is polled rather than pushed.
   *
   * There is no live channel in this deployment, and a bell that only updates
   * on a page load is a bell that is wrong most of the time. A minute is often
   * enough to be useful and rare enough not to be a request per person per
   * second.
   */
  const unread = useQuery<NotificationsResult, ApiError>({
    queryKey: ['notifications', 'count'],
    queryFn: () => api.notifications({ query: { limit: 1 } }),
    refetchInterval: 60_000,
  });
  const count = unread.data?.unread ?? 0;

  return (
    <span data-component="NotificationBell" className="relative inline-flex">
      <IconButton
        icon="Bell"
        // The label says HOW MANY rather than leaving a screen reader to
        // announce a bare digit beside a bell.
        label={
          count > 0
            ? `${t('topbar.notifications')}: ${t('notify.unread', { values: { count: formatCount(count, format) } })}`
            : t('topbar.notifications')
        }
        onClick={onOpen}
      />
      {count > 0 ? (
        <span
          data-notify-count={String(count)}
          aria-hidden
          className="absolute -end-0.5 -top-0.5 flex min-w-chip justify-center rounded-chip bg-bronze px-1 text-micro text-bronze-on"
        >
          <Num>{formatCount(count, format)}</Num>
        </span>
      ) : null}
    </span>
  );
}

/**
 * The account menu.
 *
 * Appearance, language and interface density live here - three separate
 * choices, because an Arabic interface with Latin digits and a light theme is a
 * perfectly ordinary combination.
 */
export function AvatarMenu(): JSX.Element {
  const t = useT();
  const { language } = useI18n();
  const format = useFormat();
  const { session, setPreference } = useSession();
  const { choice, setChoice } = useTheme();
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent): void => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={container} data-component="AvatarMenu" className="relative">
      <button
        type="button"
        aria-label={t('topbar.account')}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-touch w-touch items-center justify-center rounded-control"
      >
        <span className="flex h-icon w-icon items-center justify-center rounded-chip bg-sunken text-meta font-medium text-ink">
          {session.user.initials}
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute end-0 top-full z-40 mt-1 w-popover rounded-card bg-surface p-2 shadow-popover"
        >
          <div className="px-2 py-1">
            <p className="truncate text-body font-medium text-ink">{session.user.name}</p>
            <p className="truncate text-meta text-ink-meta">{session.user.email}</p>
          </div>
          <Rule className="my-2" />

          <MenuGroup label={t('account.theme')}>
            {(['light', 'dark', 'system'] as const).map((option) => (
              <MenuChoice
                key={option}
                selected={choice === option}
                label={t(`account.theme.${option}`)}
                onClick={() => {
                  setChoice(option);
                  setPreference({ theme: option });
                }}
              />
            ))}
          </MenuGroup>

          <MenuGroup label={t('account.language')}>
            {(['en', 'ar'] as const).map((option) => (
              <MenuChoice
                key={option}
                selected={language === option}
                label={t(`account.language.${option}`)}
                onClick={() => setPreference({ language: option })}
              />
            ))}
          </MenuGroup>

          {/* Region: what dates and numbers look like, which zone decides what
              "today" means, and which digits. Three choices rather than one,
              because an Arabic interface with Latin digits is an ordinary
              combination and a single "region" setting would make it
              unreachable. The API has accepted all three since it was written;
              until now nothing sent them. */}
          <MenuGroup label={t('account.region')}>
            <MenuSelect
              label={t('account.locale')}
              value={session.preferences.locale}
              data-preference="locale"
              options={OFFERED_LOCALES.map((entry) => ({ value: entry.locale, label: entry.locale }))}
              onChange={(value) => {
                const chosen = OFFERED_LOCALES.find((entry) => entry.locale === value);
                /*
                 * The numerals follow the locale unless the person has said
                 * otherwise, and the language follows it too (P-20: language
                 * derived from locale so the two cannot disagree). Somebody
                 * choosing ar-AE and then seeing an English interface would
                 * reasonably conclude the setting did nothing.
                 */
                setPreference({
                  locale: value,
                  ...(chosen ? { numerals: chosen.numerals } : {}),
                  language: value.startsWith('ar') ? 'ar' : 'en',
                });
              }}
            />
            <MenuSelect
              label={t('account.timezone')}
              value={session.preferences.timezone}
              data-preference="timezone"
              options={OFFERED_TIME_ZONES.map((zone) => ({ value: zone, label: zone }))}
              onChange={(value) => setPreference({ timezone: value })}
            />
            <div className="flex flex-col">
              <p className="px-2 pb-1 text-micro uppercase tracking-label text-ink-label">
                {t('account.numerals')}
              </p>
              {(['latn', 'arab'] as const).map((option) => (
                <MenuChoice
                  key={option}
                  selected={session.preferences.numerals === option}
                  label={t(`account.numerals.${option}`)}
                  onClick={() => setPreference({ numerals: option })}
                />
              ))}
            </div>
            {/* The setting shown as its effect. A person choosing a region is
                deciding what they will read every day, and the names of
                locales do not tell them that. */}
            <p data-region-example className="px-2 pt-1 text-meta text-ink-meta">
              {t('account.region.example', {
                values: {
                  date: formatDate(new Date(), format),
                  amount: formatMoney('1234.50', format),
                },
              })}
            </p>
          </MenuGroup>

          <MenuGroup label={t('account.density')}>
            {(['simple', 'advanced'] as const).map((option) => (
              <MenuChoice
                key={option}
                selected={session.preferences.density === option}
                label={t(`account.density.${option}`)}
                onClick={() => setPreference({ density: option })}
              />
            ))}
          </MenuGroup>

          <Rule className="my-2" />
          {/* Last, and on its own: a sign-out sitting among the preferences is
              one somebody presses while reaching for the theme. */}
          <button
            type="button"
            role="menuitem"
            data-sign-out
            disabled={leaving}
            onClick={() => {
              setLeaving(true);
              void api
                .signOutCurrentSession({})
                .catch(() => undefined)
                // A full load, not a route change: it throws away every cached
                // answer this session held, which a signed-out person must not keep.
                .finally(() => window.location.assign('/sign-in'));
            }}
            className="flex w-full items-center gap-2 rounded-control px-2 py-1 text-start text-body text-ink-600 hover:bg-sunken hover:text-ink disabled:opacity-60"
          >
            <Icon name="SignOut" size="inline" decorative />
            <span>{leaving ? t('account.signingOut') : t('account.signOut')}</span>
          </button>
          <Rule className="my-2" />
          <p className="px-2 py-1 text-meta text-ink-meta">
            {session.workspace.planLabel}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function MenuGroup({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="py-1">
      <p className="px-2 pb-1 text-micro uppercase tracking-label text-ink-label">{label}</p>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function MenuChoice({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onClick}
      className={cx(
        'flex items-center justify-between rounded-control px-2 py-1 text-start text-body',
        selected ? 'bg-bronze-tint text-bronze-text' : 'text-ink-600 hover:bg-sunken hover:text-ink',
      )}
    >
      <span>{label}</span>
      {selected ? <Icon name="Check" size="inline" decorative /> : null}
    </button>
  );
}

/**
 * A labelled select inside the account menu.
 *
 * A select rather than a submenu of radio items: six locales and eight time
 * zones as menu rows would make the account menu taller than most laptops, and
 * a native select is the control a screen reader and a keyboard already know.
 */
function MenuSelect({
  label,
  value,
  options,
  onChange,
  ...rest
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
} & Record<string, unknown>): JSX.Element {
  return (
    <label className="flex flex-col gap-0.5 px-2 py-1">
      <span className="text-micro uppercase tracking-label text-ink-label">{label}</span>
      <select
        {...rest}
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
